import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { COLORS } from '../lib/constants';

export default function NotificationBell({ userId, isRtl }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const panelRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    if (!userId) return;
    fetchNotifications();

    const channel = supabase
      .channel(`notif-${userId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` }, (payload) => {
        setNotifications(prev => [payload.new, ...prev]);
        setToast(payload.new);
        setTimeout(() => setToast(null), 5000);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(30);
    setNotifications(data || []);
  };

  const markAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    await supabase.from('notifications').update({ is_read: true }).in('id', unreadIds);
  };

  const timeAgo = (dateStr) => {
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 1) return isRtl ? 'الآن' : 'now';
    if (mins < 60) return isRtl ? `منذ ${mins} د` : `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return isRtl ? `منذ ${hrs} س` : `${hrs}h ago`;
    return isRtl ? `منذ ${Math.floor(hrs / 24)} يوم` : `${Math.floor(hrs / 24)}d ago`;
  };

  const side = isRtl ? 'left' : 'right';

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ position: 'relative', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 10, width: 36, height: 36, cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        🔔
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: -4, [side]: -4, background: COLORS.error, color: '#fff', borderRadius: '50%', minWidth: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', fontFamily: 'Cairo, sans-serif' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {toast && (
        <div onClick={() => { setToast(null); setOpen(true); }} style={{ position: 'fixed', top: 12, left: 12, right: 12, zIndex: 300, background: '#fff', borderRadius: 14, padding: 14, boxShadow: '0 8px 30px rgba(0,0,0,0.2)', border: `1px solid ${COLORS.border}`, cursor: 'pointer', direction: isRtl ? 'rtl' : 'ltr', fontFamily: 'Cairo, sans-serif' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text, marginBottom: 2 }}>🔔 {isRtl ? toast.title_ar : (toast.title_en || toast.title_ar)}</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? toast.body_ar : (toast.body_en || toast.body_ar)}</div>
        </div>
      )}

      {open && (
        <div style={{ position: 'absolute', top: 42, [side]: 0, width: 320, maxHeight: 420, overflowY: 'auto', background: '#fff', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', border: `1px solid ${COLORS.border}`, zIndex: 250, direction: isRtl ? 'rtl' : 'ltr', fontFamily: 'Cairo, sans-serif' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: COLORS.text }}>{isRtl ? 'الإشعارات' : 'Notifications'}</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: COLORS.primary, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                {isRtl ? 'تعليم الكل كمقروء' : 'Mark all read'}
              </button>
            )}
          </div>

          {notifications.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: COLORS.textMuted, fontSize: 13 }}>{isRtl ? 'لا توجد إشعارات' : 'No notifications'}</div>
          )}

          {notifications.map(n => (
            <div key={n.id} onClick={() => !n.is_read && markAsRead(n.id)} style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`, background: n.is_read ? '#fff' : COLORS.primaryLight, cursor: n.is_read ? 'default' : 'pointer' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: COLORS.text, marginBottom: 3 }}>{isRtl ? n.title_ar : (n.title_en || n.title_ar)}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>{isRtl ? n.body_ar : (n.body_en || n.body_ar)}</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted }}>{timeAgo(n.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
